import MuiLink from '@mui/material/Link';
import type { SxProps, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

const textLinkUnderlineBottom = 'calc(-0.06em - 1px)';
const textLinkUnderlineHeight = '2px';

export const textLinkEmphasisSx: SxProps<Theme> = {
  color: (theme: Theme) => theme.palette.primary.dark,
  display: 'inline-block',
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: 1.2,
  verticalAlign: 'baseline',
};

export const textLinkCompactSx: SxProps<Theme> = {
  color: (theme: Theme) => theme.palette.primary.main,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
  fontSize: 14,
  fontStyle: 'normal',
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: 1,
};

export const textLinkAnchorSx: SxProps<Theme> = {
  alignItems: 'center',
  color: (theme: Theme) => theme.palette.primary.dark,
  display: 'inline-flex',
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: 1.2,
  verticalAlign: 'baseline',
  width: 'fit-content',
};

export const textLinkInlineAnchorSx: SxProps<Theme> = {
  color: (theme: Theme) => theme.palette.primary.dark,
  display: 'inline',
  fontSize: 'inherit',
  fontStyle: 'inherit',
  fontWeight: 'inherit',
  letterSpacing: 'inherit',
  lineHeight: 'inherit',
  verticalAlign: 'baseline',
};

export const textLinkFooterSx: SxProps<Theme> = {
  alignItems: 'center',
  color: (theme: Theme) => theme.palette.primary.main,
  display: 'inline-flex',
  fontSize: 14,
  fontStyle: 'normal',
  fontWeight: 400,
  gap: '6px',
  justifyContent: 'center',
  letterSpacing: 0,
  lineHeight: 1,
  verticalAlign: 'baseline',
  whiteSpace: 'nowrap',
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
};

export const textLinkStatusActionSx: SxProps<Theme> = {
  alignSelf: 'center',
  alignItems: 'center',
  color: (theme: Theme) => theme.palette.primary.main,
  display: 'inline-flex',
  fontSize: 14,
  fontWeight: 700,
  justifyContent: 'center',
  letterSpacing: 0,
  lineHeight: 1.2,
  margin: 0,
  padding: 0,
  textAlign: 'center',
  textTransform: 'uppercase',
  verticalAlign: 'baseline',
  whiteSpace: 'nowrap',
  width: 'fit-content',
};

export const StyledTextLink = styled(MuiLink)(({ theme }) => ({
  cursor: 'pointer',
  display: 'inline',
  fontSize: 'inherit',
  fontWeight: 600,
  lineHeight: 'inherit',
  maxWidth: '100%',
  position: 'relative',
  textDecoration: 'none',
  transition: theme.transitions.create(['color'], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeInOut,
  }),
  '&:hover': {
    textDecoration: 'none',
  },
  '&:focus-visible': {
    outline: `3px solid ${theme.palette.primary.light}`,
    outlineOffset: 2,
    borderRadius: 6,
    textDecoration: 'none',
  },
  '&.MuiTypography-colorPrimary': {
    '&:hover, &:focus-visible': {
      color: theme.palette.primary.main,
    },
  },
  '&.MuiTypography-colorSecondary': {
    '&:hover, &:focus-visible': {
      color: theme.palette.secondary.main,
    },
  },
  '&.MuiTypography-colorTextPrimary': {
    '&:hover, &:focus-visible': {
      color: theme.palette.text.primary,
    },
  },
  '&.MuiTypography-colorTextSecondary': {
    '&:hover, &:focus-visible': {
      color: theme.palette.text.secondary,
    },
  },
  '&::after': {
    backgroundColor: 'currentColor',
    bottom: textLinkUnderlineBottom,
    content: '""',
    height: textLinkUnderlineHeight,
    left: 0,
    opacity: 0.62,
    position: 'absolute',
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
    width: 0,
  },
  '&:hover::after': {
    width: '100%',
  },
  '&:focus-visible::after': {
    width: '100%',
  },
  '&[aria-disabled="true"]::after, &:disabled::after': {
    display: 'none',
  },
})) as typeof MuiLink;
